import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useDTalkDetail } from '@/hooks/usePerformance';
import { DTalkHeader } from './DTalkLayout';
import { dtalkChip, getInitials, getAvatarColor } from '@/components/performance/dtalk/dtalkShared';
import { effectiveDTalkStatus } from '@/../../shared/dtalk-stats';
import { cn } from '@/lib/utils';

export function DTalkDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { email: encoded } = useParams<{ email: string }>();
  const email = encoded ? decodeURIComponent(encoded) : '';
  const { data, isLoading } = useDTalkDetail(email);
  const record: any = data?.record ?? null;

  if (isLoading) return <div className="flex items-center justify-center h-64"><WinclapLoader size={48} className="text-foreground/25" /></div>;

  const fullName = record ? `${record.employeeName} ${record.employeeLastName}`.trim() : email;
  const st = record ? dtalkChip(effectiveDTalkStatus(record), record.eligibilityStatus) : dtalkChip('not_started');

  const fields: Array<[string, any]> = record ? [
    ['estado_actual', record.estadoActual],
    ['aspiracion_principal', record.aspiracionPrincipal],
    ['aspiraciones_secundarias', (record.aspiracionesSecundarias ?? []).join(', ')],
    ['motivaciones', (record.motivaciones ?? []).join(', ')],
    ['competencias', (record.competenciasADesarrollar ?? []).join(', ')],
    ['responsabilidades', (record.responsabilidadesInteres ?? []).join(', ')],
  ] : [];
  const left = fields.slice(0, 3), right = fields.slice(3);

  return (
    <div className="space-y-5">
      <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" />{t('performance.dtalk.back_to_team')}</Button>
      <DTalkHeader title={t('performance.dtalk.detail.title')} cycleLabel={record?.cycle} />

      {!record ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">{t('performance.dtalk.detail.empty')}</CardContent></Card>
      ) : (
      <Card><CardContent className="pt-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold shrink-0 text-gray-700" style={{ backgroundColor: getAvatarColor(fullName) }}>
              {getInitials(record.employeeName, record.employeeLastName)}
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold truncate">{fullName}</p>
              <p className="text-sm text-teal-600 truncate">{[record.employeeRole, record.employeeArea].filter(Boolean).join(' · ')}</p>
            </div>
          </div>
          <span className={cn('inline-flex rounded-full px-3 py-1 text-sm font-semibold', st.badge)}>{t(st.labelKey)}</span>
        </div>

        {/* 2 columnas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
          <div className="space-y-5">{left.map(([k, v]) => <Field key={k} label={t(`performance.dtalk.detail.${k}`)} value={v} />)}</div>
          <div className="space-y-5">{right.map(([k, v]) => <Field key={k} label={t(`performance.dtalk.detail.${k}`)} value={v} />)}</div>
        </div>

        {/* Alerta + Validación */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={cn('rounded-xl border p-3', record.alerta ? 'border-red-200 bg-red-50/50 dark:bg-red-950/10' : 'border-teal-200 bg-teal-50/40 dark:bg-teal-950/10')}>
            <p className="text-sm font-semibold flex items-center gap-1.5">{record.alerta && <AlertCircle className="h-4 w-4 text-red-500" />}{t('performance.dtalk.detail.alerta')}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{record.alerta ? (record.motivosAlerta ?? []).join('; ') || '—' : t('performance.dtalk.detail.no_alert')}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 dark:bg-amber-950/10 p-3">
            <p className="text-sm font-semibold">{t('performance.dtalk.detail.validacion')}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{record.conclusionValidada ? t('performance.dtalk.detail.validated_by') : t('performance.dtalk.detail.not_validated')}</p>
          </div>
        </div>

        {/* Conclusión validada */}
        {(record.conclusionValidada || record.conclusionParaValidar) && (
          <div className="rounded-xl bg-muted/50 p-4">
            <p className="text-sm font-semibold mb-1 flex items-center gap-1.5">{record.conclusionValidada && <Check className="h-4 w-4 text-emerald-600" />}{t('performance.dtalk.detail.conclusion')}</p>
            <p className="text-sm text-foreground/90 italic">"{record.conclusionValidada ?? record.conclusionParaValidar}"</p>
          </div>
        )}
      </CardContent></Card>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: any }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="text-sm font-bold">{label}</p>
      <p className="text-sm text-muted-foreground mt-0.5">{value || t('performance.dtalk.detail.no_data')}</p>
    </div>
  );
}
